import Dexie from 'dexie';
import { CacheNames } from 'constants/enums';
import { Task } from 'graphql-common';
import { captureException } from '@lib/utils/captureException';
import { logger } from '@lib/services/logger';

Dexie.debug = 'dexie';

export const indexedDB = new Dexie(CacheNames.apiDB);
indexedDB.version(1).stores({
  [CacheNames.tasks]: 'id,createdAt,deadlineAt',
  [CacheNames.taskCompletions]: 'id',
  [CacheNames.apiCalls]: 'request',
});

export const saveToIndexedDB = async (
  storeName: string,
  data: unknown,
): Promise<void> => {
  const dataId = (data as { id?: string })?.id;
  logger.debug('Saving data to IndexedDB', {
    'indexeddb.store': storeName,
    'indexeddb.data_id': dataId,
  });
  try {
    await indexedDB.table(storeName).put(data);
  } catch (error) {
    logger.error('Failed to save data to IndexedDB', {
      'indexeddb.store': storeName,
      'indexeddb.data_id': dataId,
      'indexeddb.error': error instanceof Error ? error.message : String(error),
    });
    captureException(error);
  }
};

export const removeTableFromIndexedDB = async (
  storeName: string,
): Promise<void> => {
  logger.debug('Clearing IndexedDB table', {
    'indexeddb.store': storeName,
  });
  try {
    await indexedDB.table(storeName).clear();
  } catch (error) {
    logger.error('Failed to clear IndexedDB table', {
      'indexeddb.store': storeName,
      'indexeddb.error': error instanceof Error ? error.message : String(error),
    });
    captureException(error);
  }
};

export const removeByIdFromIndexedDB = async (
  storeName: string,
  id: string,
): Promise<void> => {
  if (id) {
    logger.debug('Removing item from IndexedDB', {
      'indexeddb.store': storeName,
      'indexeddb.data_id': id,
    });
    try {
      await indexedDB.table(storeName).delete(id);
    } catch (error) {
      logger.error('Failed to remove item from IndexedDB', {
        'indexeddb.store': storeName,
        'indexeddb.data_id': id,
        'indexeddb.error':
          error instanceof Error ? error.message : String(error),
      });
      captureException(error);
    }
  }
};

export const removeIndexedDB = async (): Promise<void> => {
  logger.info('Deleting IndexedDB database', {
    'indexeddb.database': CacheNames.apiDB,
  });
  try {
    indexedDB.close();
    await Dexie.delete(CacheNames.apiDB);
  } catch (error) {
    logger.error('Failed to delete IndexedDB database', {
      'indexeddb.database': CacheNames.apiDB,
      'indexeddb.error': error instanceof Error ? error.message : String(error),
    });
    captureException(error);
  }
};

export async function getResponseFromIDB(
  request: Request,
): Promise<Response | null> {
  try {
    const body = await request.clone().text();
    const data = await indexedDB.table(CacheNames.apiCalls).get(body);
    logger.debug(data ? 'IndexedDB cache hit' : 'IndexedDB cache miss', {
      'indexeddb.store': CacheNames.apiCalls,
      'indexeddb.cache_hit': !!data,
    });
    return data
      ? new Response(JSON.stringify(data.response), {
          headers: { 'Content-Type': 'application/json' },
        })
      : null;
  } catch (error) {
    logger.error('Failed to get response from IndexedDB', {
      'indexeddb.store': CacheNames.apiCalls,
      'indexeddb.error': error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

export async function cacheResponseInIDB(
  request: Request,
  response: Response,
): Promise<void> {
  try {
    if (response.ok) {
      const body = await request.clone().text();
      const data = await response.clone().json();
      await saveToIndexedDB(CacheNames.apiCalls, {
        request: body,
        response: data,
      });
      logger.debug('Response cached in IndexedDB', {
        'indexeddb.store': CacheNames.apiCalls,
      });
    }
  } catch (error) {
    logger.error('Failed to cache response in IndexedDB', {
      'indexeddb.store': CacheNames.apiCalls,
      'indexeddb.error': error instanceof Error ? error.message : String(error),
    });
  }
}

export const removeOutdatedTasksFromIndexedDB = async (
  taskIds: string[],
): Promise<void> => {
  if (taskIds.length) {
    logger.debug('Removing outdated tasks from IndexedDB', {
      'indexeddb.store': CacheNames.tasks,
      'indexeddb.task_count': taskIds.length,
      'indexeddb.task_ids': taskIds,
    });
    try {
      const tasksTable = indexedDB.table(CacheNames.tasks);
      await Dexie.Promise.all(taskIds.map((id) => tasksTable.delete(id)));
    } catch (error) {
      logger.error('Failed to remove outdated tasks from IndexedDB', {
        'indexeddb.store': CacheNames.tasks,
        'indexeddb.task_count': taskIds.length,
        'indexeddb.error':
          error instanceof Error ? error.message : String(error),
      });
      captureException(error);
    }
  }
};

export async function getAllTasksFromDB(): Promise<Task[]> {
  try {
    const store = indexedDB.table(CacheNames.tasks);
    const tasks = await store.toArray();
    logger.debug('Retrieved all tasks from IndexedDB', {
      'indexeddb.store': CacheNames.tasks,
      'indexeddb.task_count': tasks.length,
    });
    return tasks;
  } catch (error) {
    logger.error('Failed to retrieve tasks from IndexedDB', {
      'indexeddb.store': CacheNames.tasks,
      'indexeddb.error': error instanceof Error ? error.message : String(error),
    });
    captureException(error);
    return [];
  }
}
